import React, { useState } from "react";
import { useObserver } from "mobx-react-lite";
import { Box } from "@material-ui/core";
import { BaseModal } from "Components/Modal/BaseModal";
import { CancelButton } from "Globals/Styles/Control/Buttons";
import { useRouter } from "@shoothill/core";
import styled from "styled-components";
import MaterialTable from "material-table";
import { PatchedPagination } from "Components/Table/PatchedPagination";
import * as Defaults from "../../../../../Globals/Defaults/TableOptions";
import { ProjectVisitorsListViewModel } from "./ProjectVisitorsListViewModel";
import { ProjectVisitorsListModel } from "./ProjectVisitorsListModel";
import { VisitorModal } from "Views/Project/Modals/Modal.Styles";

interface IVisitorsModalProps {
    open?: boolean;
    onClose(): void;
    date: Date;
}

export const ProjectVisitorsListView: React.FC<IVisitorsModalProps> = (props) => {
    const { match } = useRouter();
    const { projectid } = match.params as any;
    const { open, onClose, date } = props;
    const [viewModel] = useState(() => new ProjectVisitorsListViewModel(projectid, date));

    return useObserver(() => (
        <BaseModal
            open={open}
            onClose={onClose}
            PaperComponent={VisitorModal}
            title={"Visitors Signed In"}
            actions={
                <>
                    <CancelButton onClick={onClose}>Close</CancelButton>
                </>
            }
        >
            <Box p={2}>
                <MaterialTable
                    columns={[
                        {
                            title: "",
                            field: "isSignIn",
                            align: "center",
                            render: (rowData: ProjectVisitorsListModel) =>
                                rowData.isSignIn ? (
                                    <SignInIconContainer>
                                        IN
                                        <GreenTriangleIcon />
                                    </SignInIconContainer>
                                ) : (
                                    <SignOutIconContainer>
                                        <RedTriangleIcon />
                                        OUT
                                    </SignOutIconContainer>
                                ),
                        },
                        {
                            title: "Date / time",
                            field: "visitDate",
                            render: (rowData: ProjectVisitorsListModel) => rowData.visitDateFormatted,
                        },
                        {
                            title: "Visitor",
                            field: "visitorFullName",
                            render: (rowData: ProjectVisitorsListModel) => (
                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                                    <InductionPhotoContainer>
                                        <img src={rowData.photographUrl} alt="Induction photo" />
                                    </InductionPhotoContainer>
                                    {rowData.visitorFullName}
                                </div>
                            ),
                        },
                        { title: "Employer name", field: "priorityEmployerName" },
                        {
                            title: "Last 3 digits",
                            field: "lastThreePhoneDigits",
                            render: (rowData: ProjectVisitorsListModel) => (
                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>{rowData.lastThreePhoneDigitsFormatted}</div>
                            ),
                        },
                        { title: "Inductee type", field: "visitorTypeName", align: "center" },
                        { title: "RAMS form", field: "ramsTitle", align: "center" },
                        { title: "Health problems", field: "healthProblemsFormatted", align: "center" },
                    ]}
                    components={{
                        Pagination: PatchedPagination,
                    }}
                    options={Defaults.GetDarwinTableOptionsNoSearchV2()}
                    data={viewModel.visitorsListItems}
                    isLoading={viewModel.IsLoading}
                    title=""
                />
            </Box>
        </BaseModal>
    ));
};

const InductionPhotoContainer = styled.div`
    margin-right: 20px;
    > img {
        border-radius: 50%;
        width: 32px;
        height: 32px;
        border: 1px solid #85b5cc;
    }
`;

const TriangleContainer = styled.div`
    position: relative;
    display: inline-block;
    width: 0;
    height: 0;
    border-top: 12px solid transparent;
    border-bottom: 12px solid transparent;
    ::before {
        content: "";
        position: absolute;
        top: -50px;
        border-top: 12px solid transparent;
        border-bottom: 12px solid transparent;
    }
`;

const GreenTriangleIcon = styled(TriangleContainer)`
    border-left: 16px solid #449b23;
    margin-left: 10px;
    ::before {
        left: 100px;
        transform: rotate(90deg);
    }
`;

const RedTriangleIcon = styled(TriangleContainer)`
    border-right: 16px solid #9b2323;
    margin-right: 10px;
    ::before {
        content: "";
        left: -100px;
        transform: rotate(-90deg);
    }
`;

const SignIconContainer = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    justify-content: center;
`;

const SignInIconContainer = styled(SignIconContainer)`
    color: #449b23;
`;

const SignOutIconContainer = styled(SignIconContainer)`
    color: #9b2323;
`;
